import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import VerifyIdentityButton from './VerifyIdentityButton';
import {
  IDENTITY_FAILED,
  IDENTITY_PENDING,
  IDENTITY_UNVERIFIED,
  getIdentityStatus,
  requiresIdentityVerification,
} from '../../utils/identityVerification';
import './Identity.css';

// Session-scoped so a dismissal lasts the visit but returns on the next one.
const DISMISS_KEY = 'identityVerificationBannerDismissed';

function readDismissed() {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

const IdentityVerificationBanner = ({ user }) => {
  const [dismissed, setDismissed] = useState(readDismissed);

  const status = getIdentityStatus(user);
  const applies = requiresIdentityVerification(user);
  const isPending = status === IDENTITY_PENDING;
  const needsAction =
    status === IDENTITY_UNVERIFIED || status === IDENTITY_FAILED;

  if (!applies || dismissed || (!needsAction && !isPending)) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Non-fatal: the banner simply reappears on the next render.
    }
    setDismissed(true);
  };

  return (
    <motion.div
      className="identity-banner"
      role="status"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 90, damping: 22 }}
    >
      <button
        type="button"
        className="identity-banner-close"
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        <X size={18} />
      </button>
      <div className="identity-banner-content">
        <h3>
          {status === IDENTITY_FAILED
            ? "Verification didn't go through"
            : 'Verify your identity'}
        </h3>
        <p>
          {status === IDENTITY_FAILED
            ? 'We could not confirm your identity last time. Give it another try — it only takes a couple of minutes.'
            : 'Confirm who you are to keep your account and Compliance Passport™ trusted by brokers. It takes about two minutes.'}
        </p>
      </div>
      <VerifyIdentityButton user={user} className="identity-banner-action" />
    </motion.div>
  );
};

export default IdentityVerificationBanner;
